import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Phone,
  ChevronDown,
  Menu,
  X,
  TrendingUp,
  Headset,
  Building2,
  Users,
  KeyRound,
} from "lucide-react";
import { Logo } from "./Badges";
import { Btn } from "./Elements";
import { NAV_LINKS, PARTNER_LINKS, PHONE_DISPLAY, TEL_HREF, CALENDLY } from "@/data/content";

const PARTNER_ICONS = {
  trend: TrendingUp,
  headset: Headset,
  building: Building2,
  users: Users,
  key: KeyRound,
};

export default function Header() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isHome = pathname === "/";
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const goToJoinTeam = (e) => {
    e.preventDefault();
    setOpen(false);
    if (pathname === "/") {
      document.getElementById("join-team")?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/#join-team");
    }
  };

  const baseLink = "relative pb-1 text-[13px] tracking-wide transition-colors duration-150";
  const navCls = (active) => {
    const activeCls = active
      ? "text-gold after:absolute after:-bottom-0.5 after:left-0 after:h-px after:w-full after:bg-gold"
      : isHome
      ? "text-white/90 hover:text-white"
      : "text-navy/80 hover:text-navy";
    return `${baseLink} ${activeCls}`;
  };

  return (
    <header
      className={`${isHome ? "absolute inset-x-0 top-0 z-40" : "sticky top-0 z-40 border-b border-line bg-white/85 backdrop-blur-md"}`}
      data-testid={isHome ? "header-home" : "header-inner"}
    >
      <div className="wrap flex h-20 items-center justify-between gap-6">
        <Link to="/" data-testid="header-logo-link" className="shrink-0">
          <Logo transparent={isHome} className="h-10 md:h-11 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Main navigation">
          {NAV_LINKS.slice(0, 5).map((l) => {
            const active = l.to === "/" ? isHome : pathname === l.to;
            return (
              <Link key={l.to} to={l.to} className={navCls(active)} data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}>
                {l.label}
              </Link>
            );
          })}

          <div className="relative group" data-testid="partners-dropdown">
            <button
              type="button"
              className={`${baseLink} flex items-center gap-1 ${
                isHome ? "text-white/90 group-hover:text-white" : "text-navy/80 group-hover:text-navy"
              }`}
            >
              Partners &amp; Careers
              <ChevronDown size={14} className="transition-transform duration-200 group-hover:rotate-180" />
            </button>
            <div className="absolute left-1/2 top-full z-50 hidden w-[340px] -translate-x-1/2 pt-5 group-hover:block group-focus-within:block">
              <div className="overflow-hidden rounded-2xl border border-line bg-white p-2 shadow-[0_18px_45px_rgba(18,38,74,0.14)]">
                {PARTNER_LINKS.map((item) => {
                  const Icon = PARTNER_ICONS[item.icon];
                  const isJoin = item.href === "/#join-team";
                  return (
                    <a
                      key={item.title}
                      href={item.href}
                      onClick={isJoin ? goToJoinTeam : undefined}
                      target={item.external ? "_blank" : undefined}
                      rel={item.external ? "noopener noreferrer" : undefined}
                      className="flex gap-3 rounded-xl p-3 hover:bg-cream transition-colors duration-150"
                      data-testid={`partner-${item.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                      <Icon size={20} strokeWidth={1.4} className="mt-0.5 shrink-0 text-gold" />
                      <span>
                        <span className="block text-sm font-medium text-navy">{item.title}</span>
                        <span className="mt-0.5 block text-xs text-ink/70">{item.sub}</span>
                      </span>
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </nav>

        <div className="flex items-center gap-5">
          <a
            href={TEL_HREF}
            data-testid="header-phone"
            className={`hidden md:flex items-center gap-2 text-sm font-medium transition-colors duration-150 ${
              isHome ? "text-white hover:text-gold" : "text-navy hover:text-gold"
            }`}
          >
            <Phone size={16} strokeWidth={1.75} />
            {PHONE_DISPLAY}
          </a>
          <Btn
            variant="gold"
            href={CALENDLY}
            external
            arrow={false}
            className="hidden sm:inline-flex !px-5 !py-3"
            testId="header-apply-now"
          >
            Apply Now
          </Btn>
          <button
            type="button"
            aria-label="Open menu"
            data-testid="mobile-menu-open"
            onClick={() => setOpen(true)}
            className="lg:hidden p-1"
          >
            <Menu size={26} strokeWidth={1.5} className={isHome ? "text-white" : "text-navy"} />
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-navy lg:hidden" data-testid="mobile-menu">
          <div className="flex h-20 shrink-0 items-center justify-between px-6">
            <Logo transparent className="h-9 w-auto" />
            <button type="button" aria-label="Close menu" data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="p-1">
              <X size={28} strokeWidth={1.5} className="text-white" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-6 py-4" aria-label="Mobile navigation">
            {NAV_LINKS.map((l) => {
              const active = l.to === "/" ? isHome : pathname === l.to;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  className={`block border-b border-white/10 py-4 font-serif text-2xl ${
                    active ? "text-gold" : "text-white"
                  }`}
                  data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                >
                  {l.label}
                </Link>
              );
            })}
            <p className="mt-8 text-[11px] font-medium uppercase tracking-[0.15em] text-gold">Partners &amp; Careers</p>
            <div className="mt-3 space-y-1">
              {PARTNER_LINKS.map((item) => {
                const Icon = PARTNER_ICONS[item.icon];
                const isJoin = item.href === "/#join-team";
                return (
                  <a
                    key={item.title}
                    href={item.href}
                    onClick={isJoin ? goToJoinTeam : undefined}
                    target={item.external ? "_blank" : undefined}
                    rel={item.external ? "noopener noreferrer" : undefined}
                    className="flex items-start gap-3 p-3"
                    data-testid={`mobile-partner-${item.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    <Icon size={20} strokeWidth={1.4} className="mt-0.5 shrink-0 text-gold" />
                    <span>
                      <span className="block text-sm font-medium text-white">{item.title}</span>
                      <span className="mt-0.5 block text-xs text-mist">{item.sub}</span>
                    </span>
                  </a>
                );
              })}
            </div>
          </nav>
          <div className="shrink-0 space-y-3 border-t border-white/10 p-6">
            <Btn variant="outlineWhite" href={TEL_HREF} arrow={false} className="w-full" testId="mobile-call-button">
              <Phone size={15} /> {PHONE_DISPLAY}
            </Btn>
            <Btn variant="gold" href={CALENDLY} external className="w-full" testId="mobile-apply-button">
              Apply Now
            </Btn>
          </div>
        </div>
      )}
    </header>
  );
}
